import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Repeat, Play } from "lucide-react";
import { toast } from "sonner";
import { api, fmtBRL, fmtCompetencia, extractError } from "@/lib/api";
import { DesativarRecorrenciaButton } from "@/components/financeiro/DesativarRecorrenciaButton";

export function RecorrenciasCard({ refreshKey, onChanged }) {
  const [regras, setRegras] = useState([]);

  const load = useCallback(async () => {
    try { setRegras((await api.get("/financeiro/despesas-recorrentes")).data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const reativar = async (id) => {
    try {
      await api.post(`/financeiro/despesas-recorrentes/${id}/reativar`);
      toast.success("Recorrência reativada");
      onChanged();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <Card className="p-6 rounded-sm border-border" data-testid="recorrencias-card">
      <div className="flex items-center gap-2 mb-1">
        <Repeat size={16} className="text-info" />
        <h2 className="font-heading font-bold text-xl">Regras recorrentes</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Contas fixas geradas automaticamente todo mês como Pendente.</p>
      <div className="space-y-2">
        {regras.map((r) => (
          <div key={r.id} data-testid={`recorrencia-row-${r.id}`}
            className={`flex items-center justify-between gap-3 border border-border/60 p-3 rounded-sm flex-wrap ${r.ativa ? "" : "opacity-60"}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium">{r.descricao}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Desde {fmtCompetencia(r.competencia_inicio)} · {r.competencias_geradas.length} mês(es) gerado(s)
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" data-testid={`recorrencia-status-${r.id}`}
                className={`text-[9px] uppercase tracking-widest ${r.ativa ? "border-success/40 text-success" : "text-muted-foreground"}`}>
                {r.ativa ? "Ativa" : "Desativada"}
              </Badge>
              <span className="stat-num text-base">{fmtBRL(r.valor)}</span>
              {r.ativa ? (
                <DesativarRecorrenciaButton recorrenciaId={r.id} descricao={r.descricao} onDone={onChanged}
                  testId={`recorrencia-desativar-${r.id}`} />
              ) : (
                <Button size="sm" variant="ghost" onClick={() => reativar(r.id)} data-testid={`recorrencia-reativar-${r.id}`}
                  className="rounded-sm text-info">
                  <Play size={14} className="mr-1" /> Reativar
                </Button>
              )}
            </div>
          </div>
        ))}
        {regras.length === 0 && (
          <div className="text-center py-6 text-muted-foreground text-sm border border-dashed rounded-sm">
            Nenhuma regra recorrente. Marque "Recorrência mensal" ao criar uma despesa.
          </div>
        )}
      </div>
    </Card>
  );
}
